import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {
  SelectedEntity,
  VoiceFilters,
  SavedDashboardList,
  SelectedVoiceCallFilterColumnsData,
  VoiceCallActiveTabType,
} from 'src/types/analytics-genesis';
import type {AnalyticsVoiceCallAction} from 'src/action-creators/analytics/analytics-voice-call';
import {
  SET_CURRENT_DASHBOARD_ID,
  SET_SELECTED_ENTITIES_FILTER,
  SET_SELECTED_VOICE_FILTERS,
  SET_SELECTED_DATE_RANGE,
  CLEAR_SELECTED_FILTERS,
  RECEIVE_SAVED_DASHBOARDS,
  SET_NAVIGATION_DOWN,
  SET_NAVIGATION_UP,
  RECEIVE_ALL_VOICEFLOWS_TABLE_COLUMN_SELECTION,
  RECEIVE_ALL_WORKFLOWS_TABLE_COLUMN_SELECTION,
  RECEIVE_ALL_CALL_RECORDS_TABLE_COLUMN_SELECTION,
  SET_SELECTED_VOICE_TAB,
} from 'src/action-creators/analytics/analytics-voice-call';
import {generateUUID} from 'src/utils/page-site';

export type AnalyticsVoiceCallState = {
  savedDashboards: SavedDashboardList | null;
  selectedDateRange: DateRangeWithTimezone;
  currentDashboardID: string | null | undefined;
  selectedVoiceFilters: VoiceFilters;
  selectedEntitiesFilters: Array<SelectedEntity>;
  isNavigationDown: boolean | null;
  isNavigationUp: boolean | null;
  allVoiceflowsTableColumnFilterData: SelectedVoiceCallFilterColumnsData | null;
  allWorkflowsTableColumnFilterData: SelectedVoiceCallFilterColumnsData | null;
  allCallRecordsTableColumnFilterData: SelectedVoiceCallFilterColumnsData | null;
  selectedVoiceTab: VoiceCallActiveTabType;
};

const initialState: AnalyticsVoiceCallState = {
  savedDashboards: null,
  selectedDateRange: {
    startDate: '',
    endDate: '',
    startDateUTC: '',
    endDateUTC: '',
  },
  currentDashboardID: null,
  selectedVoiceFilters: {
    workflow_status: [],
    workflow_category: [],
    voice_flow: [],
    recipient_type: [],
    workflow_nodes: [{id: generateUUID()}],
  },
  selectedEntitiesFilters: [{id: generateUUID()}],
  isNavigationDown: null,
  isNavigationUp: null,
  allVoiceflowsTableColumnFilterData: null,
  allWorkflowsTableColumnFilterData: null,
  allCallRecordsTableColumnFilterData: null,
  selectedVoiceTab: 'overview' as VoiceCallActiveTabType,
};

const analyticsVoiceCallReducer = (
  state: AnalyticsVoiceCallState = initialState,
  action: AnalyticsVoiceCallAction,
): AnalyticsVoiceCallState => {
  switch (action.type) {
    case SET_CURRENT_DASHBOARD_ID:
      console.log('SET_CURRENT_DASHBOARD_ID', action.payload);
      return {
        ...state,
        currentDashboardID: action.payload,
      };

    case SET_SELECTED_ENTITIES_FILTER:
      return {
        ...state,
        selectedEntitiesFilters: action.payload,
      };

    case SET_SELECTED_VOICE_FILTERS:
      return {
        ...state,
        selectedVoiceFilters: action.payload,
      };

    case SET_SELECTED_DATE_RANGE:
      return {
        ...state,
        selectedDateRange: action.payload,
      };

    case CLEAR_SELECTED_FILTERS:
      return {
        ...state,
        selectedVoiceFilters: {
          workflow_status: [],
          workflow_category: [],
          voice_flow: [],
          recipient_type: [],
          workflow_nodes: [{id: generateUUID()}],
        },
        currentDashboardID: null,
        selectedEntitiesFilters: [{id: generateUUID()}],
        selectedDateRange: {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
      };

    case RECEIVE_SAVED_DASHBOARDS:
      return {
        ...state,
        savedDashboards: action.payload,
      };

    case SET_NAVIGATION_DOWN:
      return {
        ...state,
        isNavigationDown: action.payload,
      };

    case SET_NAVIGATION_UP:
      return {
        ...state,
        isNavigationUp: action.payload,
      };

    case RECEIVE_ALL_VOICEFLOWS_TABLE_COLUMN_SELECTION:
      return {
        ...state,
        allVoiceflowsTableColumnFilterData: action.payload,
      };

    case RECEIVE_ALL_WORKFLOWS_TABLE_COLUMN_SELECTION:
      return {
        ...state,
        allWorkflowsTableColumnFilterData: action.payload,
      };

    case RECEIVE_ALL_CALL_RECORDS_TABLE_COLUMN_SELECTION:
      return {
        ...state,
        allCallRecordsTableColumnFilterData: action.payload,
      };

    case SET_SELECTED_VOICE_TAB:
      return {...state, selectedVoiceTab: action.payload as VoiceCallActiveTabType};
    default:
      return state;
  }
};

export default analyticsVoiceCallReducer;
